import React, { FC, useEffect, useRef, useState } from 'react';
import { CompanyInformation } from '../factoring-application/CompanyInformation';
import { OwnerInformation } from '../factoring-application/OwnerInformation';
import { UploadDocuments } from '../factoring-application/UploadDocuments';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'app/configureStore';
import SkeletonLoader from '../../components/SkeletonLoader';
import styles from './FormPageDisplay.scss';
import InsuranceInformation from '../factoring-application/InsuranceInformation';
import Navigation from '../../components/Navigation';
import SignAndSubmit from '../factoring-application/SignAndSubmit';
import SubmissionPage from '../submission/SubmissionPage';
import { BankingApplicationDocument, DO_APPLICATION_TYPE, ApplicationStatus } from 'client-experience-toolbox';
import { useApplication } from 'hooks/useApplication';
import { Authority } from 'pages/banking-application/Authority';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { DriftIcons, DriftSnackbar } from 'drift-ui';
import BusinessHistory from 'pages/banking-application/BusinessHistory';
const { Clear } = DriftIcons;
import { BusinessBackground } from '../banking-application/BusinessBackground';
import BusinessType from 'pages/banking-application/BusinessType';
import { BusinessInformation } from 'pages/banking-application/BusinessInformation';
import { BusinessOwnership } from '../banking-application/BusinessOwnership';
import IneligibilityPage from './ineligibilityPage/IneligibilityPage';
import { EsignAgreement } from '../banking-application/EsignAgreement';
import { OwnershipInformation } from '../banking-application/OwnershipInfo';
import { ControlProng } from 'pages/banking-application/ControlProng';
import { ReviewAndSubmit } from '../banking-application/ReviewAndSubmit';
import { TermsAndPrivacy } from 'pages/banking-application/TermsAndPrivacy';
import { BankingPostSubmissionDisplay } from 'pages/banking-application/BankingPostSubmissionDisplay';
import { initAlloy } from 'utils/utils';

type FormPageDisplayProps = {
  type: DO_APPLICATION_TYPE;
};

type DisplaySectionProps = {
  type: DO_APPLICATION_TYPE;
  section: number;
  showAuthority: boolean;
  showPostSubmissionDisplay: boolean;
};

const DisplaySection: FC<DisplaySectionProps> = ({ type, section, showAuthority, showPostSubmissionDisplay }) => {
  switch (type) {
    case DO_APPLICATION_TYPE.FACTORING:
      return [
        <CompanyInformation key={0} />,
        <OwnerInformation key={1} />,
        <InsuranceInformation key={2} />,
        <UploadDocuments key={3} />,
        <SignAndSubmit key={4} />,
      ][section - 1];
    case DO_APPLICATION_TYPE.BANKING:
      if (showAuthority) {
        return <Authority />;
      } else if (showPostSubmissionDisplay) {
        return <BankingPostSubmissionDisplay />;
      }
      return [
        <BusinessBackground key={0} />,
        <BusinessHistory key={1} />,
        <BusinessType key={2} />,
        <BusinessOwnership key={3} />,
        <BusinessInformation key={4} />,
        <OwnershipInformation key={5} />,
        <ControlProng key={6} />,
        <EsignAgreement key={7} />,
        <TermsAndPrivacy key={8} />,
        <ReviewAndSubmit key={9} />,
      ][section - 1];
  }
};

export const FormPageDisplay: FC<FormPageDisplayProps> = ({ type }) => {
  //Change this to state variable when more sections are added
  const { section, application, totalSections } = useApplication(type);
  const submissionStatus = useSelector((state: RootState) => state.applicationReducer.submissionStatus);
  const bankingSubmissionError = useSelector((state: RootState) => state.applicationReducer.bankingSubmissionError);
  const topRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const primaryApplicant = application?.applicationData?.applicants?.find(
    (applicant) => applicant.applicantId === application?.primaryApplicantId,
  );
  const isBanking = type === DO_APPLICATION_TYPE.BANKING;
  const showBankingSubmissionOverlay = isBanking && submissionStatus === 'submitting';
  const showPostSubmissionDisplay =
    isBanking &&
    !showBankingSubmissionOverlay &&
    (submissionStatus === 'submitted' || application?.status === ApplicationStatus.SUBMITTED);
  const isLoading = !application?.status;
  const showAuthority = isBanking && !application?.sectionsCompletion?.authority?.completedAt && !isLoading;
  const isBankingLoaded = !isLoading && isBanking;
  const [showFailedSnackbar, setShowFailedSnackbar] = useState(false);
  const patchFullApplicationError = useSelector(
    (state: RootState) => state.applicationReducer.patchFullApplicationError,
  );
  const [showPatchErrorSnackbar, setShowPatchErrorSnackbar] = useState(false);
  const dispatch = useDispatch();
  const lockedBy = (application as BankingApplicationDocument)?.lockedBy;
  const hideIcon = (section === 1 && !showAuthority) || isLoading || showPostSubmissionDisplay;
  const hideMenuBar = showAuthority || isLoading || showPostSubmissionDisplay || bankingSubmissionError;

  let customTitle: string | undefined;
  if (showAuthority) {
    customTitle = t('digital_onboarding.banking.banking_application_titles.authority');
  } else if (bankingSubmissionError) {
    customTitle = t('Error');
  } else if (showPostSubmissionDisplay) {
    customTitle = t('digital_onboarding.banking.banking_application_titles.successful_submission');
  }

  useEffect(() => {
    // scroll to top of page on section change
    topRef.current?.scrollIntoView({ behavior: 'smooth' });

    // Not sure if this is needed after calling init in Banking.tsx since we are not technically changing the page in our singe page app
    // but adding it here since the docs say to init on every page and we don't have access to Neuro ID Dashboard to verify if its all working
    if (isBanking) {
      initAlloy((application as BankingApplicationDocument)?.submissionSteps?.journeyApplicationToken);
    }
  }, [section, isBanking]);

  useEffect(() => {
    if (submissionStatus === 'failed') {
      setShowFailedSnackbar(true);
    }
  }, [submissionStatus]);

  useEffect(() => {
    if (patchFullApplicationError) {
      setShowPatchErrorSnackbar(true);
    }
  }, [patchFullApplicationError]);

  if (type === DO_APPLICATION_TYPE.FACTORING && (submissionStatus || primaryApplicant?.signedAt)) {
    return <SubmissionPage type={type} />;
  }

  if (lockedBy && isBanking) {
    return <IneligibilityPage />;
  }

  return (
    <>
      <div className={styles.fixedHeader}>
        <Navigation
          type={type}
          totalSections={totalSections}
          currentSection={section}
          hideMenuBar={hideMenuBar}
          customTitle={customTitle}
          customIcon={showAuthority && <Clear fontSize="large" color="inherit" />}
          hideIcon={hideIcon}
        />
      </div>

      <div
        className={cx(
          styles.scrollableContent,
          (showAuthority || showPostSubmissionDisplay || bankingSubmissionError) && styles.marginTop,
        )}
      >
        <div ref={topRef} className={styles.container}>
          <div className={!isBankingLoaded ? styles.content : styles.bankingContent}>
            {!isLoading ? (
              <DisplaySection
                type={type}
                section={section}
                showAuthority={showAuthority}
                showPostSubmissionDisplay={showPostSubmissionDisplay || bankingSubmissionError}
              />
            ) : (
              <SkeletonLoader />
            )}
          </div>
        </div>
      </div>
      {showBankingSubmissionOverlay && <SubmissionPage type={type} />}
      <DriftSnackbar
        open={showFailedSnackbar || showPatchErrorSnackbar}
        variant="error"
        message={
          showPatchErrorSnackbar
            ? t('digital_onboarding.error_contacting')
            : t('digital_onboarding.banking.authority.please_try_again')
        }
        autoHideDuration={3000}
        onClose={() => {
          setShowFailedSnackbar(false);
          setShowPatchErrorSnackbar(false);
          dispatch({ type: 'SET_PATCH_FULL_APPLICATION_ERROR', patchFullApplicationError: false });
        }}
      />
    </>
  );
};

export default FormPageDisplay;
